import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, navigate, location.state]);

  if (user && typeof user === "object") {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "Bejelentkezés sikertelen.");
      return;
    }
    const dest = location.state?.from?.pathname || "/dashboard";
    navigate(dest, { replace: true });
  };

  return (
    <div
      data-testid="login-page"
      className="relative flex min-h-screen w-full items-center justify-center bg-[hsl(var(--sipx-canvas))] px-6"
    >
      {/* Subtle decorative grid background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.25]"
      >
        <div className="absolute inset-0 sipx-grid [mask-image:radial-gradient(circle_at_50%_30%,black,transparent_70%)]" />
      </div>

      {/* Theme toggle in top right */}
      <button
        type="button"
        onClick={toggleTheme}
        data-testid="login-theme-toggle"
        aria-label={theme === "dark" ? "Világos téma" : "Sötét téma"}
        className="absolute right-6 top-6 grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
      >
        {theme === "dark" ? (
          <Sun className="h-4 w-4" strokeWidth={1.75} />
        ) : (
          <Moon className="h-4 w-4" strokeWidth={1.75} />
        )}
      </button>

      {/* Brand top-left */}
      <div className="absolute left-6 top-6 flex items-center gap-2.5">
        <BrandMark size={28} rounded="md" />
        <span className="text-sm font-semibold tracking-tight">SIPX Group</span>
      </div>

      <div className="relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Bejelentkezés
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            SIPX Group belső rendszer
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.02)]"
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium text-muted-foreground"
              >
                E-mail cím
              </label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nev@sipxgroup.hu"
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="text-xs font-medium text-muted-foreground"
              >
                Jelszó
              </label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-10"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="group h-10 w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Belépés…
                </>
              ) : (
                <>
                  Belépés
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} SIPX Group · Belső rendszer
        </p>
      </div>
    </div>
  );
}
